import { cdn } from '~/modules/services/image-service';
import _ from 'lodash';

import api, { buildURL } from './api-service';
import ValidationService from './validation-service';
import ProjectService from './project-service';

const CREATE_ASSET_MUTATION = `
mutation createAsset($input: AssetInput) {
	createAsset(input: $input) {
		asset {
			id
			name
			embargo
		}
	}
}
`;

export const ASSET_CONSTRAINTS = {
	name: {
		presence: {
			allowEmpty: false
		}
	},
	slug: {
		presence: {
			allowEmpty: false
		},
		format: {
			pattern: /^[a-zA-Z0-9-]+$/
		}
	},
	usage: {
		presence: {
			allowEmpty: false
		}
	},
	type: {
		presence: {
			allowEmpty: false
		}
	}
};
export const ASSET_CONSTRAINTS_SHORT = {
	name: {
		presence: {
			allowEmpty: false
		}
	},
	usage: {
		presence: {
			allowEmpty: false
		}
	},
	type: {
		presence: {
			allowEmpty: false
		}
	}
};

const AssetService = {

	createAsset(asset) {
		const params = {
			input: {
				...asset,
				...(asset.length ? { length: parseInt(asset.length, 10) } : {})
			}
		};
		return api.graphql.request(CREATE_ASSET_MUTATION, params);
	},

	validateAsset(asset) {
		return ValidationService.validate(asset, ASSET_CONSTRAINTS);
	},

	validateAssetShort(asset) {
		return ValidationService.validate(asset, ASSET_CONSTRAINTS_SHORT);
	},

	addAssetAudio(data) {
		return api.client.post('/asset-audios', data)
			.then((res) => res.data);
	},

	deleteAssetAudio(id) {
		return api.client.delete(`/asset-audios/${id}`)
			.then((res) => res.data);
	},

	getAssetAudiosList(id) {
		return api.client.get(`/asset-audios?asset=${id}`)
			.then((res) => res.data);
	},

	getBase64(thumbnailUrl) {
		if( !thumbnailUrl ) {
			return null;
		}

		return fetch(thumbnailUrl)
			.then((response) => response.blob())
			.then((blob) => {
				return new Promise((resolve, reject) => {
					const reader = new FileReader();
					reader.onloadend = () => {
						const base64 = reader.result;

						// Create an image element to get the dimensions
						const img = new Image();
						img.src = base64;

						// Once the image is loaded, extract dimensions
						img.onload = () => {
							const dimensions = { width: img.width, height: img.height };
							resolve({ base64, dimensions });
						};

						img.onerror = reject;
					};

					reader.onerror = reject;
					reader.readAsDataURL(blob);
				});
			});
	},

	getAssetProjectThumbnail(projectId) {
		return new Promise((resolve) => {
			ProjectService.getProjectById({ id: projectId })
				.then((res) => {
					if(!res) {
						resolve({
							projectId: projectId,
							base64: null
						});
					}

					let thumbnailUrl = res?.$links?.logo;
					if( thumbnailUrl ) {
						this.getBase64(thumbnailUrl)
							.then((metadata) => {
								resolve({
									projectId: projectId,
									base64: metadata.base64,
									dimensions: metadata.dimensions
								});
							})
							.catch((e) => {
								console.error('Error getting base64', e);
								resolve({
									projectId: projectId,
									base64: null,
									dimensions: null
								});
							});
					}
					else {
						resolve({
							projectId: projectId,
							base64: null,
							dimensions: null
						});
					}
				});
		});
	},

	getAssetThumbnail(assetId) {
		return new Promise((resolve) => {
			this.getAssetById(assetId)
				.then((res) => {

					if(!res) {
						resolve({
							assetId: assetId,
							base64: null,
							dimensions: null
						});
					}

					let thumbnailUrl = res?.$links?.thumbnail ?? res?.thumbnail;
					if( thumbnailUrl ) {
						this.getBase64(thumbnailUrl)
							.then((metadata) => {
								resolve({
									assetId: assetId,
									base64: metadata.base64,
									dimensions: metadata.dimensions
								});
							})
							.catch((e) => {
								console.error('Error getting base64', e);
								resolve({
									assetId: assetId,
									base64: null,
									dimensions: null
								});
							});
					}
					else {
						resolve({
							assetId: assetId,
							base64: null,
							dimensions: null
						});
					}
				});
		});
	},

	hasShareVotingPermission(asset) {
		return _.get(asset, '$permissions.access', []).includes('shareVotingView')
			|| _.get(asset, 'permissions.access', []).includes('shareVotingView');
	},

	// TODO fix autosubs variable - have a single value from the backend
	canAutosub(asset) {
		return ( _.get(asset, '$permissions.canAutosub', false)
			&& _.get(asset, '$permissions.access', []).includes('autosubs') )
				|| _.get(asset, 'permissions.access', []).includes('autosubs');
	},

	canOrder(asset) {
		// @todo remove $permissions
		return !asset.unorderable && !asset.creative && (asset?.$permissions?.access.includes('requests') || asset?.permissions?.access.includes('requests'));
	},

	canShare(asset) {
		// @todo remove $permissions
		return asset?.$permissions?.access.includes('sharing') || asset?.permissions?.access.includes('sharing');
	},

	canRequestAccess(asset, approvers) {
		return approvers?.some((approver) => approver.assetTypes?.includes(asset?.type)) ?? false;
	},

	canOrderLanguage(asset, language, withAutosubs = false) {
		let key = withAutosubs ? 'autosubs' : 'requests';
		let access = _.get(asset, '$permissions.access', []);
		let canAccess = access.indexOf(key) !== -1;

		if( !canAccess ) {
			return false;
		}

		let canOrderLanguages = _.get(asset, `$permissions.sources.${key}.languages`);
		if( !_.isArray(canOrderLanguages) ) {
			return false;
		}

		let canOrder = !canOrderLanguages.length
			|| canOrderLanguages.indexOf(language.code) !== -1;

		return canOrder;
	},

	canOrderTerritory(asset, territory, withAutosubs = false) {
		let key = withAutosubs ? 'autosubs' : 'requests';
		let access = _.get(asset, '$permissions.access', []);
		let canAccess = access.indexOf(key) !== -1;

		if( !canAccess ) {
			return false;
		}

		let canOrderTerritories = _.get(asset, `$permissions.sources.${key}.countries`);
		if( !_.isArray(canOrderTerritories) ) {
			return false;
		}

		let canOrder = !canOrderTerritories.length
			|| canOrderTerritories.indexOf(territory.code) !== -1;

		return canOrder;
	},

	getProjectAssets(projectId) {
		return api.client.get(`/assets?project=${projectId}`, { headers: { 'X-Range': 'resources=0-500' } })
			.then((res) => res.data);
	},

	getAssetsByIds(assetIds, options = {}) {

		if( options.withExtended) {
			options.$permissions = 'extended';
			delete options.withExtended;
		}

		if( !_.isEmpty(assetIds) ) {
			return api.client.get(buildURL('assets', {
				id: assetIds,
				...options
			})).then((res) => {
				return res.data;
			});
		}

	},

	getProjectAssetsByParams(projectId, queryParams) {
		function toURLSearchParams(obj) {
			// Remove undefined elements
			const cleanedObject = Object.fromEntries(
				// eslint-disable-next-line no-unused-vars
				Object.entries(obj).filter(([_, value]) => value !== undefined)
			);

			// Convert the cleaned object to a URLSearchParams instance
			const params = new URLSearchParams();

			// Add key-value pairs to URLSearchParams, handling arrays properly
			for(const [key, value] of Object.entries(cleanedObject)) {
				if(Array.isArray(value)) {
					value.forEach((item) => params.append(`${key}[]`, item));
				}
				else {
					params.append(key, value);
				}
			}

			return params;
		}
		queryParams.project = projectId;
		delete queryParams.id;
		let params = toURLSearchParams(queryParams);
		return api.client.get(`/assets?${params}`)
			.then((res) => res.data);
	},

	getProjectAssetsWithSearch(projectId, q) {
		return api.client.get(buildURL(`/assets`, {
			project: projectId,
			q
		}), { headers: { 'X-Range': 'resources=0-500' } })
			.then((res) => res.data);
	},

	getAllAssetsList(params) {
		if( !params.project ) {
			delete params.project;
		}
		return api.client.get(buildURL(`/lists/assets`, params), { headers: { 'X-Range': 'resources=0-500' } })
			.then((res) => res.data);
	},

	getSimilarAssets(params) {
		return api.client.get(buildURL(`/assets`, params), { headers: { 'X-Range': 'resources=0-200' } }).then((res) => res.data);
	},

	getAssetById(id, options = {}) {
		let url = options.withExtended
			? `/assets/${id}?$permissions=extended`
			: `/assets/${id}`;

		return api.client.get(url)
			.then((res) => res.data[0] || res.data)
			.catch((err) => {
				return Promise.reject(err);
			});
	},
	getRelatedAssets(assetId) {
		return api.client.get(`/assets/related/${assetId}`)
			.then((res) => res.data || []);
	},

	getResizedThumbnail(asset) {
		// @todo remove $links when GraphQL is used everywhere
		let thumbnailUrl = asset?.$links?.thumbnail ?? asset?.thumbnail;

		if( !thumbnailUrl ) {
			return '//via.placeholder.com/160x67?text=No%20Thumbnail';
		}

		return cdn(thumbnailUrl, { width: 160 });
	},

	getLength(asset) {
		const length = asset.length;

		if( !length ) {
			return null;
		}

		const minutes = ~~(length / 60);
		const seconds = length - (minutes * 60);

		const minutesStr = ('' + minutes).padStart(2, '0');
		const secondsStr = ('' + seconds).padStart(2, '0');

		return `${minutesStr}:${secondsStr}`;
	},

	isNew(asset) {
		return asset?.flags?.includes('new') || asset?.$flags?.includes('new');
	},

	isRecentlyUpdated(asset) {
		return asset?.flags?.includes('updated') || asset?.$flags?.includes('updated');
	},

	isPrintAsset(asset) {
		return asset.mediaType === 'image';
	},

	isRoadshowAsset(asset) {
		return asset.type === 'roadshow';
	},

	isTrailerAsset(asset) {
		return asset.type.indexOf('trailer') > -1;
	},

	updateAsset(id, data) {
		return api.client.patch(`/assets/${id}`, data)
			.then((res) => res.data);
	},

	searchAssets(query, limit=20) {
		return api.client.get(buildURL('/assets', { q: query, limit: limit }))
			.then((res) => res.data);
	},

	triggerGraphicsAnalysis(id) {
		return api.client.get(`/assets/analyze?id=${id}`).then((res) => res.data && res.status === 200);
	},

	deleteAsset(id) {
		return api.client.delete(`/assets/${id}`).then((res) => res.data);
	}
};

export default AssetService;
